import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import "@/styles/MainScreen.css";
import { HeroDiagram } from "./HeroDiagram";

const vibrantPink = "#FF80AB";
const vibrantPurple = "#9575CD";
const vibrantBlue = "#64B5F6";
const vibrantOrange = "#FFB74D";
const vibrantGreen = "#81C784";

type HeroStep = {
  word: string;
  color: string;
};

// Words double as node ids in HeroDiagram — keep them in sync.
// Ordered bottom-up through the diagram (leaf supports, then the
// middle tier, then the conclusion), so the highlight climbs the
// argument and the "thinking" crescendo lands as the cycle's finale.
const heroSteps: HeroStep[] = [
  { word: "writing", color: vibrantOrange },
  { word: "collaboration", color: vibrantGreen },
  { word: "annotation", color: vibrantPurple },
  { word: "diagramming", color: vibrantBlue },
  { word: "thinking", color: vibrantPink },
];

const Hero = () => {
  const [currentWord, setCurrentWord] = useState(0);
  const activeStep = (heroSteps[currentWord] ?? heroSteps[0])!;

  const handleLearnMore = () => {
    const featuresSection = document.getElementById("features-section");
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWord((prev) => (prev + 1) % heroSteps.length);
    }, 2400);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      className="hero-main"
      sx={{
        minHeight: "calc(100dvh - var(--landing-navbar-height, 0px))",
        display: "grid",
        gridTemplateRows: {
          xs: "auto auto auto",
          md: "auto minmax(0, 1fr) auto",
        },
        alignContent: { xs: "center", md: "stretch" },
        justifyItems: "center",
        alignItems: "center",
        rowGap: { xs: 0.85, sm: 1.15, md: 2 },
        pt: { xs: 1.1, md: 2 },
        pb: { xs: 1.1, md: 2 },
        boxSizing: "border-box",
        position: "relative",
        // Contain the pseudo's zIndex:-1 to this element; without a
        // stacking context here it would fall behind an ancestor's
        // opaque background and vanish.
        isolation: "isolate",
        // Faint canvas dot-grid so wide viewports don't read as empty
        // flanks. Radially masked: strongest around the diagram, gone
        // well before the edges — texture, not content. Sits behind
        // the hero's children (zIndex -1 within this stacking context).
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          zIndex: -1,
          pointerEvents: "none",
          backgroundImage: (theme) =>
            `radial-gradient(circle, ${alpha(
              theme.palette.text.primary,
              theme.palette.mode === "dark" ? 0.1 : 0.14
            )} 1.2px, transparent 1.2px)`,
          backgroundSize: "26px 26px",
          backgroundPosition: "center",
          maskImage:
            "radial-gradient(ellipse 55% 62% at 50% 52%, black 35%, transparent 78%)",
        },
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          px: { xs: 2, md: 0 },
          width: "100%",
          maxWidth: 1200,
        }}
      >
        <Typography
          variant="h1"
          sx={{
            mb: { xs: 0.75, md: 1.5 },
            fontSize: { xs: "2rem", sm: "2.5rem", md: "4.2rem" },
            fontWeight: 700,
            letterSpacing: "-2px",
            lineHeight: 1.05,
          }}
        >
          AnnoGram
        </Typography>
        <Typography
          component="h2"
          variant="body1"
          sx={{
            mb: { xs: 0.5, md: 1 },
            fontSize: { xs: "1.05rem", sm: "1.35rem", md: "2.6rem" },
            fontWeight: 600,
            textAlign: "center",
            minHeight: { xs: "2.5rem", sm: "3.3rem", md: "4.8rem" },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            letterSpacing: { xs: "-1px", sm: "-1.5px", md: "-2px" },
            lineHeight: 1.1,
          }}
        >
          <span className="rotating-sentence">
            <span
              style={{
                transition: "margin 0.3s",
                marginRight: activeStep.word === "thinking" ? 0 : "0.5em",
              }}
            >
              Where
            </span>
            <span
              className="rotating-word"
              style={{
                color: activeStep.color,
                fontSize: "1.15em",
                fontWeight: 700,
                margin: "0 0.25em",
                transition: "color 0.3s, font-size 0.3s",
                whiteSpace: "nowrap",
              }}
            >
              {activeStep.word}
            </span>
            <span
              style={{
                transition: "margin 0.3s",
                marginLeft: activeStep.word === "thinking" ? 0 : "0.5em",
              }}
            >
              happens.
            </span>
          </span>
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: { xs: 110, sm: 138, md: 220 },
          py: { xs: 0.5, md: 1 },
        }}
      >
        <HeroDiagram
          activeWord={activeStep.word}
          activeColor={activeStep.color}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: { xs: 0.45, md: 1 },
        }}
      >
        <Typography
          component="p"
          variant="subtitle1"
          sx={{
            mb: 0.5,
            fontWeight: 600,
            letterSpacing: 1,
            fontSize: { xs: "0.9rem", md: "1.2rem" },
          }}
        >
          Learn More
        </Typography>
        <Button
          variant="text"
          color="inherit"
          size="large"
          onClick={handleLearnMore}
          sx={{
            minWidth: 0,
            borderRadius: "50%",
            p: 1,
            background: "transparent",
            color: "text.primary",
            transition: "background 0.2s",
            "&:hover": {
              background: "action.hover",
            },
          }}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: { xs: 40, md: 50 } }} />
        </Button>
      </Box>
    </Box>
  );
};

export default Hero;
